import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  UserPlus, 
  Gear, 
  SignOut, 
  FirstAidKit,
  ClipboardText,
  Users,
  UserCircle,
  Lightning,
  CalendarCheck,
  Clock,
  MapPin
} from '@phosphor-icons/react';

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mySchedules, setMySchedules] = useState([]);

  useEffect(() => {
    async function fetchMySchedules() {
      try {
        const { data } = await axios.get(`${API_URL}/api/schedules/my`);
        // Show only upcoming (today and future)
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        setMySchedules(data.filter(s => s.date >= today).slice(0, 5));
      } catch { /* non-critical */ }
    }
    fetchMySchedules();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logos */}
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_1} 
                alt="Stand By Site Medical Services" 
                className="h-10 object-contain"
              />
              <img 
                src={LOGO_2} 
                alt="On-Site Medical Consultants" 
                className="h-10 object-contain"
              />
            </div>

            {/* User Info & Logout */}
            <div className="flex items-center gap-4">
              <Link to="/profile" data-testid="profile-link">
                <div className="text-right cursor-pointer hover:opacity-80">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role === 'qa_qi' ? 'QA/QI' : user?.role}</p>
                </div>
              </Link>
              <Link to="/profile">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="rounded-sm"
                  data-testid="profile-button"
                >
                  <UserCircle size={20} />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="rounded-sm"
                data-testid="logout-button"
              >
                <SignOut size={16} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back, {user?.first_name}</p>
        </div>

        {/* Action Cards */}
        <div className="flex flex-wrap justify-center gap-6">
          {/* Create New Patient - Hidden for QA/QI */}
          {user?.role !== 'qa_qi' && (
            <Link to="/patient/new" data-testid="create-patient-link" className="w-full sm:w-80">
              <Card className="border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center mb-4">
                    <UserPlus size={24} className="text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">Create New Patient</CardTitle>
                  <CardDescription>
                    Start a new patient assessment form
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="create-patient-button">
                    <FirstAidKit size={18} className="mr-2" />
                    New Patient Form
                  </Button>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* View All Patients - Hidden for QA/QI */}
          {user?.role !== 'qa_qi' && (
            <Link to="/patients" data-testid="view-patients-link" className="w-full sm:w-80">
              <Card className="border-slate-200 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center mb-4">
                    <ClipboardText size={24} className="text-green-600" />
                  </div>
                  <CardTitle className="text-xl">View Patients</CardTitle>
                  <CardDescription>
                    View and search all patient records
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full rounded-sm" data-testid="view-patients-button">
                    <ClipboardText size={18} className="mr-2" />
                    View Records
                  </Button>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Admin Suite - Only for admins and QA/QI */}
          {(isAdmin || user?.role === 'qa_qi') && (
            <Link to="/admin" data-testid="admin-suite-link" className="w-full sm:w-80">
              <Card className="border-slate-200 hover:border-purple-300 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center mb-4">
                    <Gear size={24} className="text-purple-600" />
                  </div>
                  <CardTitle className="text-xl">{user?.role === 'qa_qi' ? 'QA/QI Reports' : 'Admin Suite'}</CardTitle>
                  <CardDescription>
                    {user?.role === 'qa_qi' ? 'View and generate reports' : 'Manage users and generate reports'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full rounded-sm border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="admin-suite-button">
                    <Users size={18} className="mr-2" />
                    {user?.role === 'qa_qi' ? 'View Reports' : 'Admin Panel'}
                  </Button>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Command Post - For admins and QA/QI */}
          {(isAdmin || user?.role === 'qa_qi') && (
            <Link to="/command-post" data-testid="command-post-link" className="w-full sm:w-80">
              <Card className="border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-amber-100 rounded-sm flex items-center justify-center mb-4">
                    <Lightning size={24} className="text-amber-600" />
                  </div>
                  <CardTitle className="text-xl">Command Post</CardTitle>
                  <CardDescription>
                    Live event dashboard with auto-refresh
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="command-post-button">
                    <Lightning size={18} className="mr-2" />
                    Open Command Post
                  </Button>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {/* My Upcoming Schedule */}
        {mySchedules.length > 0 && (
          <div className="mt-8" data-testid="my-schedule-section">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarCheck size={20} className="text-slate-700" />
                  <CardTitle className="text-lg">My Upcoming Schedule</CardTitle>
                </div>
                <CardDescription>Your next assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mySchedules.map(s => (
                    <div key={s.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-4 py-3" data-testid={`my-schedule-${s.id}`}>
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 min-w-[100px]">{formatDate(s.date)}</span>
                        <div className="flex items-center gap-1.5">
                          <MapPin size={14} className="text-blue-500" />
                          <span className="text-sm text-slate-700">{s.event_name}</span>
                        </div>
                        {(s.start_time || s.end_time) && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock size={12} />
                            <span>{s.start_time || '—'} - {s.end_time || '—'}</span>
                          </div>
                        )}
                        {s.triage_center && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{s.triage_center}</span>}
                        {s.position && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{s.position}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
